import React, { useState } from "react";
import { Button, Card, Typography, message, Row, Col, InputNumber, Form, Input, Tabs, Divider, Upload, Alert, Progress, Switch } from "antd";
import { DataIngestService } from "../../services/DataIngestService";
import { usePermissions } from "../../PermissionsProvider";
import { DownloadOutlined, CloudUploadOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";

const { Paragraph, Text } = Typography;
const { Dragger } = Upload;

interface IngestFormValues {
  companyId: number;
  applicationRefName: string;
  actionType: string;
  batchSize: number;
  concurrentWorkers: number;
}

const IngestData: React.FC = () => {
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadForm] = Form.useForm<IngestFormValues>();
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [useLocalProcessing, setUseLocalProcessing] = useState(false);
  const { apiKey } = usePermissions();

  const uploadInitialValues: IngestFormValues = {
    companyId: null as unknown as number,
    applicationRefName: "",
    actionType: "",
    batchSize: 1000,
    concurrentWorkers: 5
  };

  const uploadAndProcessFile = async (file: File, formValues: IngestFormValues): Promise<void> => {
    if (!file) {
      message.error("No file selected");
      return;
    }
    try {
      setUploadProgress(0);
      setIsUploading(true);
      setUploadLoading(true);
      
      const response = await DataIngestService.uploadAndIngest(
        file,
        formValues.companyId,
        formValues.applicationRefName,
        formValues.actionType,
        formValues.batchSize,
        formValues.concurrentWorkers,
        useLocalProcessing,
        true, // useStreaming always
        apiKey,
        (progress) => {
          setUploadProgress(progress);
        }
      );
      
      if (response.success) {
        if (useLocalProcessing && response.request_count) {
          // For local processing, show the counts
          message.success(
            `Success! Processed ${response.request_count} requests and ${response.response_count ?? 0} responses`
          );
        } else {
          // For Kubernetes jobs, show the job scheduled message
          message.success(response.message || "File uploaded successfully. Data will start being ingested in the background.");
        }
      } else {
        throw new Error(response.error || "Unknown error occurred");
      }
    } catch (error: any) {
      // Sanitize error message to avoid exposing sensitive information
      let errorMessage = "File upload and processing failed";
      
      // Log the full error for debugging
      console.error("Error in upload and process:", error);
      
      // Extract a user-safe message - avoid showing raw errors to users
      if (error?.response?.status === 401) {
        errorMessage = "Authentication error. Please check your API key.";
      } else if (error?.response?.status === 413) {
        errorMessage = "File is too large to upload.";
      } else if (error?.response?.status >= 500) {
        errorMessage = "Server error occurred during processing. Please try again later.";
      } else if (error?.message?.includes("network") || error?.message?.includes("connection")) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      message.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadLoading(false);
      setUploadProgress(0);
      setUploadedFiles([]);
      setSelectedFile(null);
    }
  };

  // Upload props configuration - just for selecting the file, not uploading
  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    accept: ".jsonl, .json",
    fileList: uploadedFiles,
    beforeUpload: (file) => {
      if (!file.name.toLowerCase().endsWith(".jsonl") && !file.name.toLowerCase().endsWith(".json")) {
        message.error(`${file.name} is not a JSONL or JSON file`);
        return Upload.LIST_IGNORE;
      }
      
      // For large files, show a warning
      const maxSizeWarning = 500 * 1024 * 1024; // 500MB warning threshold
      if (file.size > maxSizeWarning) {
        message.warning(`File is large (${(file.size / (1024 * 1024)).toFixed(2)}MB). Upload may take some time.`);
      }
      
      setSelectedFile(file);
      setUploadedFiles([file]);
      
      return false;
    },
    onRemove: () => {
      setUploadedFiles([]);
      setSelectedFile(null);
      setUploadProgress(0);
      return true;
    }
  };

  const handleUploadAndProcess = async () => {
    if (!selectedFile) {
      message.error("Please select a file first");
      return;
    }

    try {
      const values = await uploadForm.validateFields() as IngestFormValues;
      
      await uploadAndProcessFile(selectedFile, values);
    } catch (validationError) {
      message.error("Please fill in all required fields correctly");
      console.error("Form validation error:", validationError);
    }
  };

  const jsonlExample = `{
  "session_id": "3d26c0b4-fb85-4b8e-bd5f-e0d28f956d54",
  "messages": [
    {
      "role": "system",
      "content": "System Prompt."
    },
    {
      "role": "assistant",
      "content": "Assistant Message"
    },
    {
      "role": "user",
      "content": "User Response"
    },
    {
      "role": "assistant",
      "content": "Assistant Message"
    },
    {
      "role": "user",
      "content": "User Response",
      "tool_calls": []
    },
    {
      "role": "assistant",
      "content": "Assistant Message"
    }
  ],
  "tools": [
    {
      "type": "function",
      "function": {
        "name": "tool_call_name",
        "description": "Description",
        "parameters": {
          "type": "object",
          "properties": {},
          "required": []
        }
      }
    }
  ]
}`;

  return (
    <div className="admin-page-container">
      <Card className="admin-card">
        <Typography.Title level={4} className="font-400">
          <DownloadOutlined /> JSONL Data Ingestion
        </Typography.Title>
        <Typography.Paragraph className="text-typo-paragraph">
          Import conversations from a JSONL file directly to the database through S3.
        </Typography.Paragraph>
        
        <Tabs defaultActiveKey="upload">
          <Tabs.TabPane tab="Upload" key="upload">
            <Form
              form={uploadForm}
              layout="vertical"
              initialValues={uploadInitialValues}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item 
                    name="companyId" 
                    label="Company ID" 
                    rules={[{ required: true, message: "Company ID is required" }]}
                  >
                    <InputNumber min={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    name="applicationRefName" 
                    label="Application Name" 
                    rules={[{ required: true, message: "Application name is required" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    name="actionType" 
                    label="Action Type" 
                    rules={[{ required: true, message: "Action type is required" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="batchSize" label="Batch Size" rules={[{ required: true, message: "Batch size is required" }]}>
                    <InputNumber min={10} max={1000} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="concurrentWorkers" label="Max Concurrent Workers" rules={[{ required: true, message: "Number of concurrent workers is required" }]}>
                    <InputNumber min={1} max={100} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Use Local Processing">
                    <Switch
                      checked={useLocalProcessing}
                      onChange={setUseLocalProcessing}
                    />
                    <div style={{ marginTop: "4px" }}>
                      <Typography.Text type="secondary">
                        {useLocalProcessing 
                          ? "Process directly in backend (faster for dev)" 
                          : "Schedule as Kubernetes job (for large files)"}
                      </Typography.Text>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                {!selectedFile ? (
                  <Dragger {...uploadProps} disabled={isUploading || uploadLoading}>
                    <p className="ant-upload-drag-icon">
                      <CloudUploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag JSONL file to select
                    </p>
                    <p className="ant-upload-hint">
                      Select a JSONL file containing conversation data. 
                      No upload happens until you click the "Upload & Process" button below.
                    </p>
                  </Dragger>
                ) : (
                  <div style={{ marginTop: "16px" }}>
                    <Alert
                      message="File selected"
                      description={
                        <div>
                          <p>File: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)</p>
                          <p>Click "Upload & Process" below to start uploading and processing this file.</p>
                          <Button 
                            onClick={() => {
                              setUploadedFiles([]);
                              setSelectedFile(null);
                              setUploadProgress(0);
                            }}
                            size="small"
                            style={{ marginTop: "8px" }}
                          >
                            Remove File
                          </Button>
                        </div>
                      }
                      type="info"
                      showIcon
                    />
                  </div>
                )}
                
                {isUploading && (
                  <div style={{ marginTop: "16px" }}>
                    <Progress percent={uploadProgress} status="active" />
                    <Typography.Text type="secondary">
                      Uploading and processing... {uploadProgress}%
                    </Typography.Text>
                  </div>
                )}
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  icon={<CloudUploadOutlined />}
                  onClick={handleUploadAndProcess}
                  loading={uploadLoading}
                  disabled={!selectedFile || isUploading}
                >
                  Upload & Process File
                </Button>
                <div style={{ marginTop: "8px" }}>
                  <Typography.Text type="secondary">
                    This will upload the selected file to S3 and process it
                    {useLocalProcessing 
                      ? " directly in the backend." 
                      : " in the background as a Kubernetes job."}
                  </Typography.Text>
                </div>
              </Form.Item>
            </Form>
          </Tabs.TabPane>
          
          <Tabs.TabPane tab="Example" key="example">
            <div>
              <Paragraph>
                <Text strong>Each line in your JSONL file should be a valid JSON object in this format. Tool calls are also supported in messages.</Text>
              </Paragraph>
              <Divider />
              <pre style={{ 
                backgroundColor: "#f6f6f6", 
                padding: "16px", 
                borderRadius: "4px", 
                overflowX: "auto",
                fontSize: "14px",
                lineHeight: "1.5"
              }}>
                {jsonlExample}
              </pre>
              <Divider />
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default IngestData;
